import { useBeaconFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBeaconFilters';
import { useMainEntity, useRetailerId, useRetailerSku, useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import {
  ContentMetricsRequestBuilder,
  GetContentMetricsForProductRequestBuilder,
  GetWeightedKeywordsAndPhrasesForProductRequestBuilder,
  VariationsRequestBuilder
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/ContentRequests/ContentRequestBuilder';

/**
 * For content requests, adds the category, subcategory, and segment filters
 * if they are applied
 */
export const useBaseContentRequestBuilder = () => {
  const { hasActiveFilters, appFilters } = useBeaconFilters();
  const mainEntity = useMainEntity();
  const { mainTimePeriod } = useTimePeriods();
  const retailerId = useRetailerId();

  const requestBuilder = new ContentMetricsRequestBuilder()
    // FIXME: using this for now because we don't have a clear type for the main entity
    .setMainEntity({ id: mainEntity.id, type: mainEntity.type })
    .setMainTimePeriod(mainTimePeriod)
    .setRetailerId(+retailerId);

  // TODO(tyan): figure out what other filters we care about (e.g., product level)
  if (hasActiveFilters) {
    const { categoryFilters, subcategoryFilters, segmentFilters, businessUnitFilters } = appFilters;

    if (categoryFilters) {
      requestBuilder.setCategoryIds(categoryFilters as number[]);
    }

    if (subcategoryFilters) {
      requestBuilder.setSubcategoryIds(subcategoryFilters as number[]);
    }

    // Combine segment filters and business unit filters as segmentIds
    const combinedSegmentIds: string[] = [];

    if (segmentFilters) {
      combinedSegmentIds.push(segmentFilters.id);
    }

    if (businessUnitFilters) {
      combinedSegmentIds.push(...(businessUnitFilters as string[]));
    }

    if (combinedSegmentIds.length > 0) {
      requestBuilder.setSegmentIds(combinedSegmentIds);
    }
  }

  return requestBuilder;
};

export const useContentMetricsForProductRequestBuilder = () => {
  const { mainTimePeriod } = useTimePeriods();
  const retailerId = useRetailerId();
  const retailerSku = useRetailerSku();

  const requestBuilder = new GetContentMetricsForProductRequestBuilder(
    +retailerId,
    mainTimePeriod.endWeek
  ).setRetailerSku(retailerSku);

  return requestBuilder;
};

export const useWeightedKeywordsAndPhrasesForProductRequestBuilder = () => {
  const { mainTimePeriod } = useTimePeriods();
  const retailerId = useRetailerId();
  const retailerSku = useRetailerSku();

  const requestBuilder = new GetWeightedKeywordsAndPhrasesForProductRequestBuilder(
    +retailerId,
    mainTimePeriod.endWeek
  ).setRetailerSku(retailerSku);

  return requestBuilder;
};

export const useVariationsRequestBuilder = () => {
  const { hasActiveFilters, appFilters } = useBeaconFilters();
  const mainEntity = useMainEntity();
  const { mainTimePeriod } = useTimePeriods();
  const retailerId = useRetailerId();

  const requestBuilder = new VariationsRequestBuilder()
    .setMainEntity({ id: mainEntity.id, type: mainEntity.type })
    .setMainTimePeriod(mainTimePeriod)
    .setRetailerId(+retailerId);

  // Add active filters if they exist
  if (hasActiveFilters) {
    const { categoryFilters, subcategoryFilters, segmentFilters, businessUnitFilters } = appFilters;

    if (categoryFilters) {
      requestBuilder.setCategoryIds(categoryFilters as number[]);
    }

    if (subcategoryFilters) {
      requestBuilder.setSubcategoryIds(subcategoryFilters as number[]);
    }

    // Combine segment filters and business unit filters as segmentIds
    const combinedSegmentIds: string[] = [];

    if (segmentFilters) {
      combinedSegmentIds.push(segmentFilters.id);
    }

    if (businessUnitFilters) {
      combinedSegmentIds.push(...(businessUnitFilters as string[]));
    }

    if (combinedSegmentIds.length > 0) {
      requestBuilder.setSegmentIds(combinedSegmentIds);
    }
  }

  return requestBuilder;
};
